import React, { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { parseDateLocal } from '../../services/dateService';
import { DashboardProject, ProgressRow } from './dashboardModel';

import {
    G_BODY_TOP, G_COL, G_MONTH_Y, G_PAD, TODAY_BADGE_H, TODAY_BADGE_W, TextMeasure,
    approxTextWidth, daysFromToday, fitText, fracToX, ganttBar, ganttLayout, ganttRowBox, ganttYear, monthLabel, todayBadgeX, weekFractions,
} from './ganttGeometry';

interface Props {
    rows: ReadonlyArray<ProgressRow>;
    onOpen: (project: DashboardProject) => void;
    setServiceOverride: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
    /** Kein Hover auf Touch. */
    isMobile: boolean;
    /** Bezugsjahr und -tag der Achse; kommen von aussen, damit das Memo nur einmal am Tag rechnet. */
    todayMs: number;
    /** Vollbild: fuellt die Flaeche in Breite und Hoehe; sonst auf 60vh gedeckelt. */
    fullscreen?: boolean;
    /** Legende ueber dem Diagramm; im Vollbild steht sie in der Kopfzeile. */
    showLegend?: boolean;
}

/** Legende des Zeitstrahls — auch fuer die Kopfzeile des Vollbilds. */
export const GanttLegend: React.FC<{ className?: string }> = ({ className = '' }) => (
    <div className={`flex items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 font-semibold flex-wrap ${className}`}>
        <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded-sm" style={{ background: G_COL.low }} />&lt;50%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded-sm" style={{ background: G_COL.mid }} />50–89%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded-sm" style={{ background: G_COL.high }} />≥90%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded-sm" style={{ background: G_COL.planning }} />Planung</span>
        <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded-sm" style={{ background: G_COL.onHold }} />Ruht</span>
        <span className="flex items-center gap-1 ml-1"><span className="w-px h-3 bg-red-500" />Heute</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500 opacity-60" />Mehrere Pos.</span>
    </div>
);

// ── Textbreite mit der echten UI-Schrift ──────────────────────────────────
// Ein Canvas fuer alle Instanzen, die Schriftfamilie einmal gelesen (sie
// aendert sich zur Laufzeit nicht). Ohne Canvas bleibt es bei der Schaetzung.
let sharedCtx: CanvasRenderingContext2D | null | undefined;
let uiFontFamily = '';
const canvasCtx = () => {
    if (sharedCtx === undefined) {
        try {
            sharedCtx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
            if (sharedCtx) uiFontFamily = getComputedStyle(document.body).fontFamily || 'sans-serif';
        } catch {
            sharedCtx = null;
        }
    }
    return sharedCtx;
};
const measureWith = (weight: number): TextMeasure => (s, size) => {
    const ctx = canvasCtx();
    if (!ctx) return approxTextWidth(s, size);
    ctx.font = `${weight} ${size}px ${uiFontFamily}`;
    return ctx.measureText(s).width;
};
const measureSemibold = measureWith(600);
const measureRegular = measureWith(400);

/**
 * Einmal neu zeichnen, sobald die Webschrift geladen ist — vorher misst der
 * Canvas mit der Ersatzschrift, und die Kuerzungen stimmen nicht.
 */
const useRedrawWhenFontsReady = () => {
    const [, setReady] = useState(false);
    useEffect(() => {
        const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
        if (!fonts) return;
        let alive = true;
        fonts.ready.then(() => { if (alive) setReady(true); }).catch(() => {});
        return () => { alive = false; };
    }, []);
};

/**
 * Der Zeitstrahl „Meine Projekte": ein Jahr als Achse, je Projekt ein Balken
 * mit Fortschritt der Turnusposition, Hover-Karte mit allen Angaben.
 *
 * Gezeichnet wird in echten Pixeln: der Container wird gemessen
 * (ResizeObserver), die viewBox ist genau so breit. Mehr Platz verlaengert
 * die Zeitachse, die Schrift bleibt 11px.
 *
 * Vier SVGs statt einem: Namensspalte und Zeitachse getrennt, Monatsleiste
 * und Zeilen getrennt. So klebt die Monatsleiste oben und die Namensspalte
 * links, wenn der Container scrollt (Handy hochkant: die Achse laeuft
 * seitlich weg, die Namen bleiben stehen). Alle vier rechnen in denselben
 * Koordinaten — die Zeitachsen-SVGs beginnen ihre viewBox bei x = label.
 */
export const ProjectGantt: React.FC<Props> = ({ rows, onOpen, setServiceOverride, isMobile, todayMs, fullscreen = false, showLegend = true }) => {
    // Hover-Karte des Zeitstrahls. Die Position wird nur beim Betreten der Zeile
    // gesetzt (nicht bei jeder Mausbewegung), sonst rendert das SVG dauernd neu.
    const [ganttHover, setGanttHover] = useState<{ p: ProgressRow; x: number; y: number } | null>(null);

    // Innenmass des Scroll-Containers (ohne Scrollleisten). Als Ref-Callback,
    // damit schon vor dem ersten Zeichnen gemessen wird und das Diagramm
    // nicht erst in Grundbreite aufblitzt.
    const [size, setSize] = useState({ w: 0, h: 0 });
    const measureRef = useCallback((el: HTMLDivElement | null) => {
        if (!el) return;
        const read = () => {
            const w = el.clientWidth, h = el.clientHeight;
            setSize(s => (s.w === w && s.h === h ? s : { w, h }));
        };
        read();
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(read);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Inline-Kachel und Vollbild stehen gleichzeitig im DOM — die IDs von
    // Muster und Clip muessen je Instanz eindeutig sein.
    const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
    const clipId = `gantt-clip-${uid}`, patternId = `gantt-plan-${uid}`;
    useRedrawWhenFontsReady();

    const L = ganttLayout({ width: size.w, availableHeight: fullscreen ? size.h : undefined, rowCount: rows.length, fullscreen });
    const axis = ganttYear(todayMs);
    const box = ganttRowBox(L.row);
    const monthW = L.track / 12;
    const trackW = L.width - L.label;
    const todayX = fracToX(L, (todayMs - axis.start) / axis.range);
    const badgeX = todayBadgeX(L, todayX);
    // Wochenstriche nur, wenn sie nicht zu einem Kamm verschmelzen.
    const weeks = L.track / 52 >= 10 ? weekFractions(axis) : [];
    const clearHover = () => setGanttHover(null);

    const lines = rows.map((p, i) => {
        const y = G_BODY_TOP + i * L.row;
        const hasMulti = p.allInspServices.length > 1;
        // Status als Kuerzel-Plakette rechts in der Spalte; der Name wird auf den Rest gekuerzt.
        const statusTag = p.status === 'planning' ? 'P' : p.status === 'on_hold' ? 'R' : '';
        const dotW = hasMulti ? 10 : 0;
        const tagW = statusTag ? 16 : 0;
        const nameRight = L.label - G_PAD - dotW - (tagW ? tagW + 5 : 0);
        const labelText = p.number ? `${p.number} ${p.name}` : p.name;
        const metaText = `${p.ct > 0 ? `${p.us}/${p.ct} · ${p.pct}%` : (p.status === 'planning' ? 'Planung' : p.status === 'on_hold' ? 'Pausiert' : 'Keine Pos.')}${hasMulti ? ` · ⇄ ${p.selectedServiceTitle}` : ''}`;
        const isHovered = ganttHover?.p.id === p.id;
        const onEnter = (e: React.MouseEvent) => {
            if (isMobile) return;
            const rowRect = (e.currentTarget as SVGGElement).getBoundingClientRect();
            setGanttHover(prev => (prev?.p.id === p.id ? prev : { p, x: e.clientX, y: rowRect.top + rowRect.height / 2 }));
        };
        return {
            p, i, y, hasMulti, statusTag, tagW, nameRight, isHovered, onEnter,
            name: fitText(labelText, nameRight - G_PAD, 11, measureSemibold),
            meta: fitText(metaText, L.label - G_PAD * 2 - dotW, 11, measureRegular),
            dim: p.status === 'on_hold' ? '0.5' : '1',
        };
    });

    // Klick in die Namensspalte wechselt bei mehreren Begehungs-Positionen die
    // Position; sonst (und auf der Zeitachse immer) oeffnet er das Projekt.
    const onLabelClick = (p: ProgressRow) => {
        if (p.allInspServices.length > 1) {
            const curIdx = p.allInspServices.findIndex(s => s.id === p.selectedServiceId);
            const nextIdx = (curIdx + 1) % p.allInspServices.length;
            setServiceOverride(prev => ({ ...prev, [p.id]: p.allInspServices[nextIdx].id }));
        } else {
            onOpen(p.project);
        }
    };

    return (
        <div className={fullscreen ? 'flex-1 min-h-0 flex flex-col px-2 sm:px-3 pt-2 pb-2' : 'px-1.5 pt-1.5 pb-1.5'}>
            {showLegend && <GanttLegend className="px-1 pb-2" />}
            <div
                ref={measureRef}
                className={`overflow-auto overscroll-contain ${fullscreen ? 'flex-1 min-h-0' : 'max-h-[60vh]'}`}
                onScroll={clearHover}
                onMouseLeave={clearHover}
            >
                {rows.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-xs text-slate-500">Keine Projekte</div>
                ) : (
                    <div style={{ width: L.width }}>
                        <div className="sticky top-0 z-[2] flex" onMouseEnter={clearHover} aria-hidden="true">
                            <svg width={L.label} height={L.head} viewBox={`0 0 ${L.label} ${L.head}`} className="block shrink-0 sticky left-0 z-[1]">
                                <rect x={0} y={0} width={L.label} height={L.head} fill="#ffffff" />
                                <text x={G_PAD} y={G_MONTH_Y} style={{ fontSize: '11px' }} className="font-semibold fill-slate-400">Projekt</text>
                                <text x={L.label - G_PAD} y={G_MONTH_Y} textAnchor="end" style={{ fontSize: '11px' }} className="font-semibold fill-slate-400 tabular">{axis.year}</text>
                                <line x1={0} y1={L.head - 0.5} x2={L.label} y2={L.head - 0.5} stroke="#e2e8f0" strokeWidth="1" />
                                <line x1={L.label - 0.5} y1={0} x2={L.label - 0.5} y2={L.head} stroke="#dbe2ec" strokeWidth="1" />
                            </svg>
                            <svg width={trackW} height={L.head} viewBox={`${L.label} 0 ${trackW} ${L.head}`} className="block shrink-0">
                                <rect x={L.label} y={0} width={trackW} height={L.head} fill="#ffffff" />
                                {Array.from({ length: 12 }, (_, i) => {
                                    const x = fracToX(L, i / 12);
                                    return (
                                        <g key={i}>
                                            <rect x={x} y={0} width={monthW} height={L.head} fill={i % 2 === 0 ? '#fafbfc' : '#f1f5f9'} />
                                            <text x={x + monthW / 2} y={G_MONTH_Y} textAnchor="middle" style={{ fontSize: '11px' }} className="font-semibold fill-slate-500">{monthLabel(i, monthW)}</text>
                                        </g>
                                    );
                                })}
                                {weeks.map(f => { const x = fracToX(L, f); return <line key={f} x1={x} y1={L.head - 5} x2={x} y2={L.head} stroke="#cbd5e1" strokeWidth="1" />; })}
                                <line x1={L.label} y1={L.head - 0.5} x2={L.width} y2={L.head - 0.5} stroke="#e2e8f0" strokeWidth="1" />
                                {/* Heute: Plakette in der unteren Spur, die Linie laeuft darunter weiter */}
                                <rect x={badgeX} y={L.head - TODAY_BADGE_H - 1} width={TODAY_BADGE_W} height={TODAY_BADGE_H} rx="4" fill={G_COL.today} />
                                <text x={badgeX + TODAY_BADGE_W / 2} y={L.head - 5} textAnchor="middle" style={{ fontSize: '11px' }} className="fill-white font-semibold">Heute</text>
                                <line x1={todayX} y1={L.head - 1} x2={todayX} y2={L.head} stroke={G_COL.today} strokeWidth="1.5" opacity="0.7" />
                            </svg>
                        </div>
                        <div className="flex">
                            {/* Namensspalte: klebt links, deckender Grund, damit die Balken darunter wegscrollen */}
                            <svg width={L.label} height={L.bodyHeight} viewBox={`0 0 ${L.label} ${L.bodyHeight}`} className="block shrink-0 sticky left-0 z-[1]">
                                <defs>
                                    {/* Harte Grenze: kein Projektname darf je in die Zeitachse ragen. */}
                                    <clipPath id={clipId}><rect x={0} y={0} width={L.label - 2} height={L.bodyHeight} /></clipPath>
                                </defs>
                                <rect x={0} y={0} width={L.label} height={L.bodyHeight} fill="#ffffff" />
                                {lines.map(r => (
                                    <g key={r.p.id} className="cursor-pointer" onClick={() => onLabelClick(r.p)} onMouseEnter={r.onEnter} opacity={r.dim}>
                                        {/* Namensspalte einen Hauch heller absetzen, damit die Achse rechts als eigener Bereich liest */}
                                        <rect x={0} y={r.y} width={L.label} height={L.row} fill={r.isHovered ? '#e7f0ff' : r.i % 2 === 0 ? '#fcfdfe' : '#f6f8fb'} />
                                        <g clipPath={`url(#${clipId})`}>
                                            <text x={G_PAD} y={r.y + box.nameY} style={{ fontSize: '11px' }} className={`font-semibold ${r.p.status === 'planning' ? 'fill-blue-700' : r.p.status === 'on_hold' ? 'fill-amber-700' : 'fill-slate-800'}`}>{r.name}</text>
                                            <text x={G_PAD} y={r.y + box.metaY} style={{ fontSize: '11px' }} className="fill-slate-500">{r.meta}</text>
                                            {r.statusTag && (<>
                                                <rect x={r.nameRight + 5} y={r.y + box.tagY} width={r.tagW} height={14} rx="3" fill={r.statusTag === 'P' ? G_COL.planning : G_COL.onHold} opacity="0.16" />
                                                <text x={r.nameRight + 5 + r.tagW / 2} y={r.y + box.tagY + 10.5} textAnchor="middle" style={{ fontSize: '11px' }} className={`font-semibold ${r.statusTag === 'P' ? 'fill-blue-700' : 'fill-amber-700'}`}>{r.statusTag}</text>
                                            </>)}
                                        </g>
                                        {r.hasMulti && <circle cx={L.label - 6} cy={r.y + box.center} r="2.5" fill={G_COL.mid} opacity="0.6" />}
                                    </g>
                                ))}
                                <line x1={L.label - 0.5} y1={0} x2={L.label - 0.5} y2={L.bodyHeight} stroke="#dbe2ec" strokeWidth="1" />
                            </svg>
                            <svg width={trackW} height={L.bodyHeight} viewBox={`${L.label} 0 ${trackW} ${L.bodyHeight}`} className="block shrink-0">
                                <defs>
                                    <pattern id={patternId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="6" stroke={G_COL.planning} strokeWidth="2" /></pattern>
                                </defs>
                                {lines.map(({ p, i, y, isHovered, onEnter, dim }) => {
                                    const sMs = p.start ? new Date(p.start).getTime() : NaN, eMs = p.end ? new Date(p.end).getTime() : NaN;
                                    const bar = ganttBar(L, Number.isFinite(sMs) ? sMs : null, Number.isFinite(eMs) ? eMs : null, axis);
                                    const uw = bar.width * (p.pct / 100);
                                    // Farbe nach Status und Fortschritt
                                    const col = p.status === 'planning' ? G_COL.planning : p.status === 'on_hold' ? G_COL.onHold : p.pct >= 90 ? G_COL.high : p.pct >= 50 ? G_COL.mid : G_COL.low;
                                    const barOpacity = p.status === 'on_hold' ? '0.25' : '0.45';
                                    return (
                                        <g key={p.id} className="cursor-pointer" onClick={() => onOpen(p.project)} onMouseEnter={onEnter} opacity={dim}>
                                            <rect x={L.label} y={y} width={trackW} height={L.row} fill={isHovered ? '#eef5ff' : i % 2 === 0 ? '#fff' : '#fafbfc'} />
                                            <rect x={bar.x} y={y + box.barY} width={bar.width} height={box.barH} rx="4" fill={col} opacity="0.12" />
                                            {p.ct > 0 && <rect x={bar.x} y={y + box.barY} width={uw} height={box.barH} rx="4" fill={col} opacity={barOpacity} />}
                                            <rect x={bar.x} y={y + box.barY} width={bar.width} height={box.barH} rx="4" fill="none" stroke={col} strokeWidth="0.8" opacity="0.45" />
                                            {/* Schraffur fuer Planung (noch kein Fortschritt) */}
                                            {p.status === 'planning' && <rect x={bar.x} y={y + box.barY} width={bar.width} height={box.barH} rx="4" fill={`url(#${patternId})`} opacity="0.15" />}
                                        </g>
                                    );
                                })}
                                {/* Monatsraster ueber den Zeilen, sonst uebermalen die Zeilenflaechen es */}
                                {Array.from({ length: 12 }, (_, i) => { const x = fracToX(L, i / 12); return <line key={`grid-${i}`} x1={x} y1={0} x2={x} y2={L.bodyHeight} stroke="#0f172a" strokeWidth="0.5" opacity="0.06" />; })}
                                {/* Heute-Linie zuletzt, damit sie ueber allen Zeilen liegt */}
                                <line x1={todayX} y1={0} x2={todayX} y2={L.bodyHeight} stroke={G_COL.today} strokeWidth="1.5" strokeDasharray="3 2" opacity="0.7" />
                            </svg>
                        </div>
                    </div>
                )}
            </div>
            {/* Hover-Karte: alle Angaben zur Zeile, ohne das Projekt zu oeffnen.
                Per Portal an <body> und `fixed` — kein scrollender Container und
                kein transformiertes Vollbild darf sie abschneiden oder versetzen.
                Die Zeile wird frisch nachgeschlagen: nach einem Positionswechsel
                per Klick zeigt die Karte sofort den neuen Stand. */}
            {ganttHover && typeof document !== 'undefined' && createPortal(
                <HoverCard hover={{ ...ganttHover, p: rows.find(r => r.id === ganttHover.p.id) ?? ganttHover.p }} todayMs={todayMs} />,
                document.body,
            )}
        </div>
    );
};

const Row = ({ k, v }: { k: string; v: React.ReactNode }) => (
    <div className="flex gap-2 text-[11px] leading-snug"><span className="text-slate-400 w-[68px] shrink-0">{k}</span><span className="text-slate-700 font-medium min-w-0 break-words">{v}</span></div>
);

const HoverCard: React.FC<{ hover: { p: ProgressRow; x: number; y: number }; todayMs: number }> = ({ hover, todayMs }) => {
    const hp = hover.p;
    const CARD_W = 324, CARD_H = 320;
    const vw = typeof window === 'undefined' ? 1280 : window.innerWidth;
    const vh = typeof window === 'undefined' ? 800 : window.innerHeight;
    // Rechts vom Zeiger, sonst links — und nie ueber den Fensterrand hinaus.
    const left = hover.x + 20 + CARD_W > vw - 12 ? Math.max(12, hover.x - 20 - CARD_W) : hover.x + 20;
    const top = Math.min(Math.max(12, hover.y - 70), Math.max(12, vh - CARD_H - 12));
    const fmt = (d?: string | null) => { const dt = parseDateLocal(d); return dt ? dt.toLocaleDateString('de-DE') : '—'; };
    const sD2 = parseDateLocal(hp.start), eD2 = parseDateLocal(hp.end);
    const days = sD2 && eD2 ? Math.max(0, Math.round((eD2.getTime() - sD2.getTime()) / 864e5)) : null;
    const rest = daysFromToday(eD2, todayMs);
    const dueD = parseDateLocal(hp.due);
    const dueIn = daysFromToday(dueD, todayMs);
    const stat = hp.status === 'planning' ? { l: 'Planung', c: 'bg-blue-50 text-blue-700 border-blue-200' }
        : hp.status === 'on_hold' ? { l: 'Ruht', c: 'bg-amber-50 text-amber-700 border-amber-200' }
            : { l: hp.sifa ? 'Laufend' : 'Ausführung', c: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    return (
        <div className="fixed z-[600] pointer-events-none animate-fade-in" style={{ left, top, width: CARD_W }}>
            <div className="bg-white rounded-xl shadow-xl overflow-hidden">
                <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/70">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            {hp.number && <div className="text-[11px] font-semibold text-slate-400">#{hp.number}</div>}
                            <div className="text-xs font-semibold text-slate-800 break-words">{hp.name || '—'}</div>
                        </div>
                        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${stat.c}`}>{stat.l}</span>
                    </div>
                    {hp.address && <div className="text-[11px] text-slate-500 mt-0.5 break-words">{hp.address}</div>}
                </div>
                <div className="px-3 py-2 space-y-1 border-b border-slate-100">
                    <Row k="Zeitraum" v={`${fmt(hp.start)} – ${fmt(hp.end)}${days !== null ? ` · ${days} Tage` : ''}`} />
                    {rest !== null && <Row k="Restlaufzeit" v={rest >= 0 ? `noch ${rest} ${rest === 1 ? 'Tag' : 'Tage'}` : <span className="text-red-600">seit {Math.abs(rest)} {rest === -1 ? 'Tag' : 'Tagen'} überfällig</span>} />}
                    <Row k="Nächste" v={dueD
                        ? <span className={dueIn !== null && dueIn < 0 ? 'text-red-600' : dueIn === 0 ? 'text-emerald-600' : ''}>{fmt(hp.due)}{dueIn !== null ? ` · ${dueIn < 0 ? `${Math.abs(dueIn)} ${dueIn === -1 ? 'Tag' : 'Tage'} überfällig` : dueIn === 0 ? 'heute' : dueIn === 1 ? 'morgen' : `in ${dueIn} Tagen`}` : ''}</span>
                        : <span className="text-slate-400">kein Termin</span>} />
                </div>
                <div className="px-3 py-2 border-b border-slate-100">
                    <div className="text-[11px] font-semibold text-slate-400 uppercase mb-1">Begehungen</div>
                    {hp.allInspServices.length === 0 ? <div className="text-[11px] text-slate-400">Keine Begehungs-Position</div> : hp.allInspServices.map(s => {
                        const sp = s.ct > 0 ? Math.round(s.us / s.ct * 100) : 0;
                        const sel = s.id === hp.selectedServiceId;
                        return (
                            <div key={s.id} className="mb-1 last:mb-0">
                                <div className="flex items-center justify-between gap-2 text-[11px]">
                                    <span className={`truncate ${sel ? 'font-semibold text-slate-800' : 'text-slate-500'}`}>{sel ? '▸ ' : ''}{s.title}</span>
                                    <span className="shrink-0 font-semibold text-slate-600">{s.us}/{s.ct} · {sp}%</span>
                                </div>
                                <div className="h-1 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, sp)}%`, background: sp >= 90 ? G_COL.high : sp >= 50 ? G_COL.mid : G_COL.low }} />
                                </div>
                                {s.ct > s.us && <div className="text-[11px] text-slate-400">offen: {s.ct - s.us}</div>}
                            </div>
                        );
                    })}
                </div>
                {hp.otherServices.length > 0 && (
                    <div className="px-3 py-2 border-b border-slate-100">
                        <div className="text-[11px] font-semibold text-slate-400 uppercase mb-1">Weitere Leistungen</div>
                        {hp.otherServices.slice(0, 5).map((s, si) => (
                            <div key={si} className="flex items-center justify-between gap-2 text-[11px]">
                                <span className="truncate text-slate-500">{s.title}</span>
                                <span className={`shrink-0 font-semibold ${s.us >= s.ct ? 'text-emerald-600' : 'text-slate-600'}`}>{s.us}/{s.ct}</span>
                            </div>
                        ))}
                        {hp.otherServices.length > 5 && <div className="text-[11px] text-slate-400 mt-0.5">+{hp.otherServices.length - 5} weitere</div>}
                    </div>
                )}
                <div className="px-3 py-2 space-y-1">
                    {hp.customer && <Row k="Auftraggeber" v={hp.customer} />}
                    {hp.client && hp.client !== hp.customer && <Row k="Bauherr" v={hp.client} />}
                    {hp.reps.length > 0 && <Row k="Zuständig" v={hp.reps.join(', ')} />}
                </div>
                <div className="px-3 py-1.5 bg-slate-50 text-[11px] text-slate-400 border-t border-slate-100">
                    Klick öffnet das Projekt{hp.allInspServices.length > 1 ? ' · Klick auf den Namen wechselt die Begehungs-Position' : ''}
                </div>
            </div>
        </div>
    );
};
